import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  getHistorySubcontractorByFiltersQuery,
  createHistorySubcontractorCompanyQuery,
  editHistorySubcontractorCompanyQuery,
  deleteHistorySubcontractorCompanyQuery,
} from "./queries";
import { useUserContext } from "api/user/UserProvider";
import {
  ACTIONS,
  HISTORY_SUBCONTRACTOR_BY_FILTERS_KEYS_ID,
  QUERY_STATUS,
} from "constants/constants";

const SubcontractorCompanyDatatrackContext = createContext({});

export const useSubcontractorCompanyDatatrackContext = () =>
  useContext(SubcontractorCompanyDatatrackContext);

export const SubcontractorCompanyDatatrackProvider = ({ ...props }) => {
  const { userScope } = useUserContext();

  const [currentAction, setCurrentAction] = useState({
    type: null,
    status: QUERY_STATUS.INITIAL,
  });

  const [historySubCompany, setHistorySubCompany] = useState([]);

  const getHistorySubcontractorByFilters = useCallback(
    async (idValue, idKey) => {
      try {
        const result = await getHistorySubcontractorByFiltersQuery({
          scope: userScope,
          idKey:
            idKey || HISTORY_SUBCONTRACTOR_BY_FILTERS_KEYS_ID.subcontractorId,
          idValue,
        });
        setHistorySubCompany(result);
        return result;
      } catch (error) {
        console.error("getHistorySubcontractorByFilters", error);
        throw error;
      }
    },
    [userScope]
  );

  const createHistorySubcontractorCompany = useCallback(
    async (data) => {
      setCurrentAction({
        type: ACTIONS.CREATE,
        status: QUERY_STATUS.LOADING,
      });

      try {
        const result = await createHistorySubcontractorCompanyQuery({
          scope: userScope,
          data,
        });
        setHistorySubCompany(result);
        setCurrentAction({
          type: ACTIONS.CREATE,
          status: QUERY_STATUS.SUCCESS,
        });
        await getHistorySubcontractorByFilters(data.subcontractor_id);
        return true;
      } catch (error) {
        setCurrentAction({
          type: ACTIONS.CREATE,
          status: QUERY_STATUS.ERROR,
        });
        console.error("createHistorySubcontractorCompany", error);
        throw error;
      }
    },
    [userScope, getHistorySubcontractorByFilters]
  );

  const editHistorySubcontractorCompany = useCallback(
    async ({ id, data }) => {
      setCurrentAction({
        type: ACTIONS.EDIT,
        status: QUERY_STATUS.LOADING,
      });
      try {
        const result = await editHistorySubcontractorCompanyQuery({
          scope: userScope,
          id,
          data,
        });
        setHistorySubCompany(result);
        await getHistorySubcontractorByFilters(id);
        setCurrentAction({
          type: ACTIONS.EDIT,
          status: QUERY_STATUS.SUCCESS,
        });
      } catch (error) {
        setCurrentAction({
          type: ACTIONS.EDIT,
          status: QUERY_STATUS.ERROR,
        });
        console.error("editHistorySubcontractorCompany", error);
        throw error;
      }
    },
    [userScope, getHistorySubcontractorByFilters]
  );

  const deleteHistorySubcontractorCompany = useCallback(
    async (id) => {
      setCurrentAction({
        type: ACTIONS.DELETE,
        status: QUERY_STATUS.LOADING,
      });
      try {
        const result = await deleteHistorySubcontractorCompanyQuery({
          scope: userScope,
          id,
        });
        setHistorySubCompany(result);
        await getHistorySubcontractorByFilters(id);
        setCurrentAction({
          type: ACTIONS.DELETE,
          status: QUERY_STATUS.SUCCESS,
        });
      } catch (error) {
        setCurrentAction({
          type: ACTIONS.DELETE,
          status: QUERY_STATUS.ERROR,
        });
        console.error("deleteHistorySubcontractorCompany", error);
        throw error;
      }
    },
    [userScope, getHistorySubcontractorByFilters]
  );

  const values = useMemo(
    () => ({
      historySubCompany,
      currentAction,
      createHistorySubcontractorCompany,
      editHistorySubcontractorCompany,
      deleteHistorySubcontractorCompany,
      getHistorySubcontractorByFilters,
      setHistorySubCompany,
      setCurrentAction,
    }),
    [
      historySubCompany,
      currentAction,
      createHistorySubcontractorCompany,
      editHistorySubcontractorCompany,
      deleteHistorySubcontractorCompany,
      getHistorySubcontractorByFilters,
    ]
  );

  return (
    <SubcontractorCompanyDatatrackContext.Provider value={values} {...props} />
  );
};
